import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/client';
import {
  quoteFinancial,
  type OperationDetail,
  type QuoteFinancial,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';

/**
 * Apoio do financeiro da cotacao (P-COT-4): o cache unico do quadro do Resumo e do
 * painel de emissao, o que a pessoa pode fazer e a leitura das regras pendentes que o
 * servidor devolve no 409. Ver docs/ADR/0051-financeiro-da-cotacao.md.
 *
 * Nada aqui calcula: parcelas, participacoes e marcas sao as informadas por alcada.
 */

/** As permissoes finas do financeiro, alem da porta da edicao da operacao. */
export const permissoesDoFinanceiro = {
  financeiro: 'operacoes.cotacao_financeiro.update',
  resseguro: 'operacoes.cotacao_resseguro.update',
  parametros: 'operacoes.cotacao_parametros.update',
} as const;

/** A chave do cache do financeiro da cotacao, a mesma para o Resumo e a emissao. */
export function chaveDoFinanceiro(operationId: string) {
  return ['operations', operationId, 'financial'] as const;
}

/** O financeiro da cotacao, lido uma vez para o Resumo e para a emissao. */
export function useFinanceiroDaCotacao(operationId: string, enabled = true) {
  return useQuery({
    queryKey: chaveDoFinanceiro(operationId),
    queryFn: ({ signal }) => quoteFinancial.get(operationId, signal),
    enabled,
  });
}

/**
 * O que a pessoa pode fazer com o financeiro: a permissao fina e a etapa. Quem decide e
 * o servidor; a tela so desabilita o que ele recusaria.
 */
export function usePermissoesDoFinanceiro(operacao: OperationDetail) {
  const { can } = useAuth();
  const porta = !operacao.is_terminal && can('operacoes.operacao.update');
  return {
    porta,
    financeiro: porta && can(permissoesDoFinanceiro.financeiro),
    resseguro: porta && can(permissoesDoFinanceiro.resseguro),
    parametros: can(permissoesDoFinanceiro.parametros),
  };
}

/** As regras pendentes que o servidor devolveu no 409, quando ha. */
export function regrasPendentesDoErro(erro: unknown): string[] {
  if (!(erro instanceof ApiError)) return [];
  const regras = erro.details?.pending_rules;
  return Array.isArray(regras)
    ? regras.filter((r): r is string => typeof r === 'string')
    : [];
}

/** Salva o arquivo recebido com o nome indicado. */
export function salvarArquivoDoFinanceiro(conteudo: Blob, nome: string) {
  const url = URL.createObjectURL(conteudo);
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  document.body.append(ancora);
  ancora.click();
  ancora.remove();
  URL.revokeObjectURL(url);
}

/**
 * A forma de pagamento gravada (P-NCT-L), com o aviso quando o meio saiu do cadastro:
 * o rotulo e o do momento da escolha.
 */
export function formaDePagamento(
  dados: Pick<QuoteFinancial, 'payment_method_id' | 'payment_method_label'>,
): string {
  if (!dados.payment_method_label) return 'Nao informada';
  return dados.payment_method_id
    ? dados.payment_method_label
    : `${dados.payment_method_label} (excluida do cadastro)`;
}

/**
 * O nome do resumo, pela cotacao e pelo tipo do arquivo que o servidor mandou. Basta o
 * numero: a lista da corretora tambem baixa o resumo ("Imprimir cotacao").
 */
export function nomeDoResumo(
  operacao: Pick<OperationDetail, 'proposal_number' | 'number'>,
  conteudo: Blob,
) {
  const numero = operacao.proposal_number ?? String(operacao.number);
  const extensao = conteudo.type.includes('pdf') ? 'pdf' : 'docx';
  return `resumo-da-cotacao-${numero}.${extensao}`;
}
